/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Feb 2023
 * @flow
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classes from './UpdateBooking.module.css';
import Button from '../../../../components/Button';
import strings from '../../../../globalization';
import TextInput from '../../../../components/TextInput';
import { CountryPicker, getPickerData } from "../../../../components/CountryPicker";
import FireboardUtils from '../../../../util/fireboard-util';
import StepInput from '../StepInput';

const EmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const defaultInputFields = {
  nameEnabled: true,
  nameRequired: true,
  phoneNumberEnabled: true,
  phoneNumberRequired: true,
  emailAddressEnabled: true,
  emailAddressRequired: true,
};

const UpdateBooking = ({ booking, onClosePopup, onUpdateBooking }) => {
  const [selectedCountry, setSelectedCountry] = useState({});
  const [state, setState] = useState({
    phoneNumber: '',
    name: '',
    emailAddress: '',
    partySize: '',
    note: '',
    comment: '',
  });

  const { storeInfo, deviceApi, currencyInfo } = useSelector(state => state);
  const { deviceGroup } = deviceApi?.data || {};
  const {
    nameEnabled,
    nameRequired,
    phoneNumberEnabled,
    phoneNumberRequired,
    emailAddressEnabled,
    emailAddressRequired,
  } = deviceGroup?.inputFields || defaultInputFields;
  const { iso2 } = currencyInfo || {};

  useEffect(() => {
    if (Object.keys(booking).length) {
      const countriesData = getPickerData();
      let defaultCountry = {};
      if (booking?.countryCode) {
        const dialCode = `+${booking.countryCode}`;
        defaultCountry = countriesData.find(country => country.dialCode === dialCode) || {};
      } else {
        defaultCountry = countriesData.find(country => country.iso2 === String(iso2).toLowerCase()) || {};
      }
      if (Object.keys(defaultCountry).length === 0) {
        defaultCountry = {
          dialCode: "+1",
          image: "/static/media/us.e4a75107.png",
          iso2: "us",
          key: 0,
          label: "United States"
        };
      }
      const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(booking.phoneNumber, defaultCountry.iso2);
      setState({
        ...state,
        phoneNumber: formattedPhoneNumber,
        name: booking.name,
        emailAddress: booking.emailAddress,
        partySize: String(booking.partySize),
        note: booking.note,
        comment: booking.comment,
      });
      setSelectedCountry(defaultCountry);
    }
  }, [booking]);

  const onCountryChange = country => {
    const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(state.phoneNumber, country.iso2);
    setSelectedCountry(country);
    setState({
      ...state,
      phoneNumber: formattedPhoneNumber,
    });
  };

  const onChangeInput = (key, value) => {
    setState({
      ...state,
      [key]: value,
    })
  };

  const onSubmit = () => {
    try {
      const { id: storeId } = storeInfo?.data || {};
      const payload = {
        storeId,
        countryCode: state.phoneNumber ? selectedCountry.dialCode.split('+')[1] : '',
        phoneNumber: state.phoneNumber ? FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2) : '',
        name: state.name.trim() || '',
        emailAddress: state.emailAddress.trim() || '',
        note: state.note.trim() || '',
        comment: state.comment.trim() || '',
        partySize: +state.partySize,
        businessDate: booking.businessDate,
        bookingDate: booking.bookingDate,
        completeDate: booking.completeDate,
        status: booking.status,
        stationId: booking.stationId,
        memberId: booking.memberId,
        id: booking.id,
      };
      onUpdateBooking && onUpdateBooking(payload);
    } catch (error) {
      console.error(error);
    }
  };

  const isSubmitDisabled = () => {
    let isEmailValid = true, isNameValid = true, isPhoneNumberValid = true;
    if (emailAddressEnabled && (emailAddressRequired || (!emailAddressRequired && state.emailAddress))) {
      isEmailValid = EmailRegex.test(state.emailAddress.trim().toLowerCase());
    }
    if (nameEnabled && (nameRequired || (!nameRequired && state.name))) {
      isNameValid = ((state.name).trim()).length > 0;
    }
    if (phoneNumberEnabled && (phoneNumberRequired || (!phoneNumberRequired && state.phoneNumber))) {
      isPhoneNumberValid = (FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)).length >= 10;
    }
    const isPartySizeValid = state.partySize.length > 0;
    return !(isEmailValid && isNameValid && isPhoneNumberValid && isPartySizeValid);
  };

  const isInputDisabled = () => {
    return booking?.status === 7 || booking?.status === 9;
  };

  return (
    <div>
      <div className={classes.headerWrapper}>
        <div className={classes.headerTxt}>{strings.updateWaitList}</div>
        <div className={classes.closeBtn} onClick={onClosePopup}>X</div>
      </div>
      <div className={classes.contentWrapper}>
        {phoneNumberEnabled && (
          <React.Fragment>
            <div>
              <label className={classes.label}>{strings.countryCode}</label>
              <CountryPicker
                selectedCountry={selectedCountry}
                onCountryChange={isInputDisabled() ? null : onCountryChange}
                containerStyle={classes.input}
              />
            </div>
            <TextInput
              placeholder={`${strings.placeholderPhone} ${phoneNumberRequired ? '' : '(optional)'}`}
              type='tel'
              value={FireboardUtils.inputFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)}
              onChange={value => onChangeInput('phoneNumber', value)}
              inputStyle={classes.input}
              disabled={isInputDisabled()}
            />
          </React.Fragment>
        )}
        {nameEnabled && (
          <TextInput
            placeholder={`${strings.nameLabel} ${nameRequired ? '' : '(optional)'}`}
            type='text'
            value={state.name}
            onChange={value => onChangeInput('name', value)}
            inputStyle={classes.input}
            disabled={isInputDisabled()}
          />
        )}
        <StepInput
          placeholder={strings.partySize}
          type='number'
          value={state.partySize}
          onChange={value => onChangeInput('partySize', value)}
          inputStyle={classes.input}
          disabled={isInputDisabled()}
        />
        {emailAddressEnabled && (
          <TextInput
            placeholder={`${strings.emailAddressPlaceholder} ${emailAddressRequired ? '' : '(optional)'}`}
            type='text'
            value={state.emailAddress}
            onChange={value => onChangeInput('emailAddress', value)}
            inputStyle={classes.input}
            disabled={isInputDisabled()}
          />
        )}
        <TextInput
          placeholder={`${strings.noteLabel} (optional)`}
          type='text'
          value={state.note}
          onChange={value => onChangeInput('note', value)}
          inputStyle={classes.input}
          disabled={isInputDisabled()}
        />
        <TextInput
          placeholder={`${strings.staffComment} (optional)`}
          type='text'
          value={state.comment}
          onChange={value => onChangeInput('comment', value)}
          inputStyle={classes.input}
        />
      </div>
      <div className={classes.buttonsWrapper}>
        <Button className={classes.button} onClick={onClosePopup}>
          <div className={classes.buttonText}>{strings.cancel}</div>
        </Button>
        <Button className={classes.button} onClick={onSubmit} disabled={isSubmitDisabled()}>
          <div className={classes.buttonText}>{strings.submit}</div>
        </Button>
      </div>
    </div>
  );
};

export default UpdateBooking;

UpdateBooking.propTypes = {
  booking: PropTypes.object,
  onClosePopup: PropTypes.func,
  onUpdateBooking: PropTypes.func,
};

UpdateBooking.defaultProps = {
  booking: {},
  onClosePopup: () => { },
  onUpdateBooking: () => { },
};
